import React, { useEffect, useState } from 'react';
import { getCsrfToken } from '../../lib/csrf.js';
import { router } from '@inertiajs/react';
import AuthLayout from '../../components/AuthLayout.jsx';

export default function BuyComplete({ email: initialEmail, buyer_type, buyer_first_name, buyer_last_name, token }) {
  const isGroupManager = buyer_type === 'group_manager';

  const [firstName, setFirstName] = useState(buyer_first_name || '');
  const [lastName, setLastName] = useState(buyer_last_name || '');
  const [email, setEmail] = useState(initialEmail || '');
  const emailLocked = !!initialEmail;
  const [password, setPassword] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const csrfToken = getCsrfToken();
    fetch('/api/buy_draft', {
      method: 'DELETE',
      headers: { 'X-CSRF-Token': csrfToken },
      credentials: 'same-origin',
    }).catch(() => {});
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    const payload = {
      password,
      password_confirmation: passwordConfirmation,
    };

    if (!emailLocked) payload.email = email;

    if (!isGroupManager) {
      payload.first_name = firstName;
      payload.last_name = lastName;
    }

    router.post(`/buy/complete/${token}`, payload, {
      onError: (errors) => {
        setError(Object.values(errors).flat().join(', '));
        setSubmitting(false);
      },
      onFinish: () => setSubmitting(false),
    });
  };

  const inputClass = "mt-2 block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6";

  return (
    <AuthLayout>
      <div className="flex min-h-full flex-col justify-center px-6 py-12 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <div className="flex justify-center mb-6">
            <img src="/logo.png" alt="123CEU" className="h-12 w-auto" />
          </div>

          <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4">
            <p className="text-sm text-green-800 font-medium text-center">Payment successful! Create a password to finish setting up your account.</p>
          </div>

          <h2 className="text-center text-2xl/9 font-bold tracking-tight text-gray-900">Set up your account</h2>
        </div>

        <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label htmlFor="email" className="block text-sm/6 font-medium text-gray-900">Email</label>
              {emailLocked ? (
                <input id="email" type="email" value={email} readOnly
                  className={`${inputClass} bg-gray-50 text-gray-500`} />
              ) : (
                <input id="email" type="email" required autoComplete="email" placeholder="you@example.com"
                  value={email} onChange={(e) => setEmail(e.target.value)} className={inputClass} />
              )}
            </div>

            {!isGroupManager && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="first_name" className="block text-sm/6 font-medium text-gray-900">First Name</label>
                  <input id="first_name" type="text" required autoComplete="given-name" placeholder="John"
                    value={firstName} onChange={(e) => setFirstName(e.target.value)} className={inputClass} />
                </div>
                <div>
                  <label htmlFor="last_name" className="block text-sm/6 font-medium text-gray-900">Last Name</label>
                  <input id="last_name" type="text" required autoComplete="family-name" placeholder="Doe"
                    value={lastName} onChange={(e) => setLastName(e.target.value)} className={inputClass} />
                </div>
              </div>
            )}

            <div>
              <label htmlFor="password" className="block text-sm/6 font-medium text-gray-900">Password</label>
              <p className="text-xs text-gray-500">Minimum 8 characters</p>
              <input id="password" type="password" required autoComplete="new-password" placeholder="Enter a secure password"
                maxLength={72} value={password} onChange={(e) => setPassword(e.target.value)} className={inputClass} />
            </div>

            <div>
              <label htmlFor="password_confirmation" className="block text-sm/6 font-medium text-gray-900">Confirm Password</label>
              <input id="password_confirmation" type="password" required autoComplete="new-password" placeholder="Confirm your password"
                maxLength={72} value={passwordConfirmation} onChange={(e) => setPasswordConfirmation(e.target.value)} className={inputClass} />
            </div>

            <button type="submit" disabled={submitting}
              className="w-full rounded-md bg-indigo-600 px-3 py-2.5 text-sm/6 font-semibold text-white shadow-sm hover:bg-indigo-700 cursor-pointer disabled:opacity-50">
              {submitting ? 'Creating account…' : 'Create account'}
            </button>
          </form>
        </div>
      </div>
    </AuthLayout>
  );
}
